import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

interface ToolPageLayoutProps {
  icon: LucideIcon
  title: string
  description: string
  formats: readonly string[]
  children: ReactNode
}

/** Shared tool page shell: header with icon, title, supported formats + content. */
export function ToolPageLayout({ icon: Icon, title, description, formats, children }: ToolPageLayoutProps) {
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:py-16 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-7 w-7" aria-hidden />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground md:text-base">{description}</p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {formats.map((format) => (
            <span
              key={format}
              className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
            >
              {format}
            </span>
          ))}
        </div>

        <div className="mt-10 space-y-6">{children}</div>
      </div>
    </div>
  )
}
